import React from 'react'

import { ActionsProvider } from '../../../../providers/ActionsProvider/ActionsProvider'
import { TooltipProvider } from '../../../../providers/TooltipProvider/TooltipProvider'
import { useModal } from '../../../../providers/ModalProvider/ModalProvider'

import { Confirm } from '../../../ModalContent'
import { SeatMap } from '../../../SeatMap'
import {
  getVenueSeatScale,
  isNmsuStyleSeatmapVenue,
} from '../../../../utilities/frontendSeatmapSizing'

const getHoldLabel = (holdInfo) => {
  if (!holdInfo || holdInfo === true) return null
  if (holdInfo.categoryName && holdInfo.holdName) {
    return `${holdInfo.holdName} (${holdInfo.categoryName})`
  }
  return holdInfo.holdName || holdInfo.categoryName || null
}

const formatHoldNames = (holdLabels) => {
  const uniqueLabels = [...new Set(holdLabels.filter(Boolean))]
  if (uniqueLabels.length === 0) return null
  if (uniqueLabels.length === 1) return uniqueLabels[0]
  if (uniqueLabels.length === 2)
    return `${uniqueLabels[0]} and ${uniqueLabels[1]}`
  return `${uniqueLabels.slice(0, -1).join(', ')}, and ${uniqueLabels.at(-1)}`
}

export default function GroupTicketsMap({
  data,
  background,
  selectedSeatIds,
  setSelectedSeatIds,
  isMapDisabled,
  soldMap,
  reservedMap,
  killMap,
  holdMap,
  openGAModal,
  event,
}) {
  const { openModal, closeModal } = useModal()

  const SELECTED_COLOR = '#141416' // black - selecting seats on map
  const INCLUDED_COLOR = '#FFA500'
  const HOLD_COLOR = '#EF466F'
  const KILL_COLOR = '#B1B5C4'
  const SOLD_COLOR = '#45B26B' // green for sold
  const RESERVED_COLOR = '#ee00f3'
  const venueSlug = event?.venue?.slug || event?.venueSlug || event?.venue_slug
  const venueName = event?.venue?.name || event?.venueName || event?.venue_name
  const useFrontendSeatSizing = isNmsuStyleSeatmapVenue(venueSlug)
  const seatScale = getVenueSeatScale({ venueName, venueSlug })

  const isSeatUnavailable = (seatId) =>
    Boolean(reservedMap?.get(seatId) || soldMap?.get(seatId))

  const isSeatSelected = (seatId) =>
    Boolean(selectedSeatIds?.seats && selectedSeatIds.seats[seatId] === seatId)

  const applySeatSelection = (seatIds, { forceSelect = false } = {}) => {
    setSelectedSeatIds((prevSelected) => {
      const newSelected = { ...prevSelected }
      if (!newSelected.seats) {
        newSelected.seats = {}
      }

      seatIds.forEach((id) => {
        if (!id || isSeatUnavailable(id)) return

        const currentlySelected = newSelected.seats[id] === id
        if (forceSelect) {
          newSelected.seats[id] = id
          return
        }

        if (currentlySelected) {
          delete newSelected.seats[id]
        } else {
          newSelected.seats[id] = id
        }
      })

      if (Object.keys(newSelected.seats).length === 0) {
        delete newSelected.seats
      }

      return newSelected
    })
  }

  const confirmHeldSeatSelection = (seatIds, holdLabels) => {
    const holdNames = formatHoldNames(holdLabels)
    const isSingle = seatIds.length === 1
    const description = holdNames
      ? isSingle
        ? `This seat is on hold for "${holdNames}". Would you like to continue and select it for assignment?`
        : `Some of these seats are on hold for "${holdNames}". Would you like to continue and select them for assignment?`
      : isSingle
        ? 'This seat is currently on hold. Would you like to continue and select it for assignment?'
        : 'Some of these seats are currently on hold. Would you like to continue and select them for assignment?'

    openModal(
      Confirm,
      {
        title: isSingle ? 'Seat on hold' : 'Seats on hold',
        description,
        confirmLabel: 'Yes',
        onConfirm: () => {
          applySeatSelection(seatIds, { forceSelect: true })
          closeModal()
        },
        onClose: closeModal,
      },
      { className: 'modal-md', contentClassName: '' }
    )
  }

  // save regular seated seats,,,
  const selectRegularSeats = (seatId, seatIdArray) => {
    // if map is disabled don't select seats
    if (isMapDisabled) return

    const seatIds = seatId ? [seatId] : seatIdArray || []
    const seatsToToggle = []
    const heldSeatsToAdd = []
    const holdLabels = []

    seatIds.forEach((id) => {
      if (!id || isSeatUnavailable(id)) return

      if (isSeatSelected(id)) {
        seatsToToggle.push(id)
        return
      }

      const holdInfo = holdMap?.get(id)
      if (holdInfo) {
        heldSeatsToAdd.push(id)
        holdLabels.push(getHoldLabel(holdInfo))
        return
      }

      seatsToToggle.push(id)
    })

    if (seatsToToggle.length > 0) {
      applySeatSelection(seatsToToggle)
    }

    if (heldSeatsToAdd.length > 0) {
      confirmHeldSeatSelection(heldSeatsToAdd, holdLabels)
    }
  }

  // GA sections use this to get their color
  const getSectionState = (section) => {
    if (
      selectedSeatIds &&
      selectedSeatIds.gaSeats &&
      selectedSeatIds.gaSeats.hasOwnProperty(section.sectionId)
    ) {
      return { disabled: false, color: SELECTED_COLOR }
    }

    return { disabled: false, color: INCLUDED_COLOR }
  }

  // for sold seats return sold color + disabled
  const getSeatState = (seatId) => {
    if (reservedMap?.get(seatId)) {
      return { disabled: true, color: RESERVED_COLOR }
    }

    if (soldMap?.get(seatId)) {
      return { disabled: true, color: SOLD_COLOR }
    }

    if (selectedSeatIds?.seats && selectedSeatIds.seats[seatId] === seatId) {
      return { disabled: false, color: SELECTED_COLOR }
    }

    if (killMap?.get(seatId)) {
      return { disabled: false, color: KILL_COLOR }
    }

    if (holdMap?.get(seatId)) {
      // Hold seats stay selectable so admins can override with confirmation
      return { disabled: false, color: HOLD_COLOR }
    }

    return { disabled: false, color: INCLUDED_COLOR }
  }

  const getSeatTooltipDetails = (seatId) => {
    const seatIdentity = reservedMap?.get(seatId) || soldMap?.get(seatId)
    if (seatIdentity && seatIdentity !== true) return seatIdentity

    const holdInfo = holdMap?.get(seatId)
    const holdLabel = getHoldLabel(holdInfo)
    if (holdLabel) {
      return { holdName: holdLabel }
    }

    return null
  }

  // This is triggered for BOTH GA and seated:
  // ( since you can select seated by selecting the entire section )
  const handleSectionSelect = (section) => {
    // if map is disabled don't select sections
    if (isMapDisabled) return

    if (section?.zoomable) {
      // Seated-section
      const seatIds = getAllSeatsInSeatedSection(section, data.rows, data.seats)
      selectRegularSeats(undefined, seatIds)
    } else {
      // GA-section
      openGAModal(section)
    }
  }

  const getAllSeatsInSeatedSection = (section, rows, seats) => {
    return Object.values(rows)
      .filter((row) => row.sectionId === section.sectionId)
      .flatMap((row) => row.seats)
      .filter((seatId) => {
        if (!seats[seatId]) return false
        return true
      })
  }

  const legendItems = [
    { label: 'Sold', className: 'sold' },
    { label: 'Reserved', className: 'reserved' },
    { label: 'Selectable', className: 'selectable' },
    { label: 'Selected', className: 'selected' },
    { label: 'Hold', className: 'hold' },
    { label: 'Kill', className: 'kill' },
  ]

  // Lets not show anything for now ( since the packages assign map does not either )
  const inventoryInfoBasedOnMapType = {
    displayInventoryInfo: false,
    deriveInventoryInfoFrom: [
      { inventory: event?.offers, type: 'offers' },
      { inventory: event?.inventory.holds, type: 'holds' },
      { inventory: event?.inventory.kills, type: 'kills' },
    ],
  }

  /*
    Customize this file ( or the files above ) in any which way that suits your purposes
    Just DO-NOT customize anything lower than this
  */

  return (
    <div style={{ width: '100%', height: '100vh', overflow: 'hidden' }}>
      {/* Seatmap fills all available space, so its also behind the Navigation now */}
      <TooltipProvider>
        <ActionsProvider canEdit={!isMapDisabled} legendItems={legendItems}>
          <SeatMap
            data={data}
            background={background}
            isMapDisabled={isMapDisabled}
            useFrontendSeatSizing={useFrontendSeatSizing}
            seatScale={seatScale}
            // Handling clicks
            selectRegularSeats={selectRegularSeats}
            handleSectionSelect={handleSectionSelect}
            // Fill Colors
            getSectionState={getSectionState}
            getSeatState={getSeatState}
            getSeatTooltipDetails={getSeatTooltipDetails}
            // For inventory "bar"
            displayInventoryInfo={
              inventoryInfoBasedOnMapType.displayInventoryInfo
            }
            deriveInventoryInfoFrom={
              inventoryInfoBasedOnMapType.deriveInventoryInfoFrom
            }
          />
        </ActionsProvider>
      </TooltipProvider>
    </div>
  )
}
